import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { Fragment, Suspense, useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, xcss } from '@atlaskit/primitives';
import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';
import { AISources, useAnalytics } from '../../common';
import { SainAction, useSAINUsageCriteriaMet } from '../../common/controller/analytics';
import { QueryIntent } from '../../constants';
import { PersonKnowledgeCard, TeamKnowledgeCard } from '../../knowledge-cards';
import { AIAnswerBody } from '../ai-answer-body';
import { useAIAnswerContext } from '../ai-answer-context';
import { SearchPageSmartAnswerFooter } from '../ai-answer-footer';
import { AIAnswerContentWaryAndBrandSection } from '../ai-answer-footer/ai-answer-footer';
import { AIAnswerLoadingStream } from '../ai-answer-loading/ai-answer-loading-stream';
import GeneratingAnimation from '../ai-answer-loading/generating-animation';
import { aiAnswerCollapseClicked, aiAnswerExpandClicked, aiAnswerExpandShown, onAIAnswerLoadingDialogShown, onAIAnswerSourceClick } from '../analytics';
import { NLPSearchResultFormat } from '../types';
import { Collapse } from './collapse';
import { messages } from './messages';
import { AIAnswerSearchBorder, GeneratingContainer, GeneratingText } from './styled';
var MAX_SOURCES = 4;
var spacerStyles = xcss({
  // eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
  lineHeight: '14px',
  paddingTop: 'space.250'
});
var errorContainerStyles = xcss({
  paddingTop: 'space.100'
});
var sourcesContainerStyles = xcss({
  paddingTop: '0',
  paddingRight: 'space.300',
  paddingBottom: 'space.150',
  paddingLeft: 'space.300'
});
export var SmartAnswerLoadingDialog = function SmartAnswerLoadingDialog() {
  var _useAIAnswerContext = useAIAnswerContext(),
    commonAttributes = _useAIAnswerContext.commonAttributes,
    partialResponse = _useAIAnswerContext.partialResponse;
  var _useAnalytics = useAnalytics(),
    _useAnalytics2 = _slicedToArray(_useAnalytics, 1),
    fireAnalyticsEvent = _useAnalytics2[0];
  var _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage;
  useEffect(function () {
    fireAnalyticsEvent(onAIAnswerLoadingDialogShown(commonAttributes));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // no need for the Collapse component when showing the ... generating animation
  if (!partialResponse || (partialResponse === null || partialResponse === void 0 ? void 0 : partialResponse.format) !== NLPSearchResultFormat.MARKDOWN) {
    return /*#__PURE__*/React.createElement(AIAnswerSearchBorder, {
      isLoading: true,
      isStreaming: false
    }, /*#__PURE__*/React.createElement(GeneratingContainer, null, /*#__PURE__*/React.createElement(GeneratingAnimation, {
      alt: formatMessage(messages.ai_answer_generating_icon_alt_text)
    }), /*#__PURE__*/React.createElement(GeneratingText, null, /*#__PURE__*/React.createElement(FormattedMessage, messages.ai_answer_generating_rovo))), /*#__PURE__*/React.createElement(AIAnswerContentWaryAndBrandSection, null));
  }
  return /*#__PURE__*/React.createElement(AIAnswerSearchBorder, {
    isLoading: true,
    isStreaming: true
  }, /*#__PURE__*/React.createElement(Suspense, {
    fallback: /*#__PURE__*/React.createElement(Fragment, null)
  }, /*#__PURE__*/React.createElement(Collapse, {
    collapsed: true,
    isLoading: true,
    isStreaming: true
  }, /*#__PURE__*/React.createElement(AIAnswerLoadingStream, {
    markdown: partialResponse.partialNlpResult
  })), /*#__PURE__*/React.createElement(AIAnswerContentWaryAndBrandSection, null)));
};

// TODO: Remove KnowledgeCard block when cleaning kd_smart_answers_render_by_experience fg
var KnowledgeCard = function KnowledgeCard() {
  var _useSAINUsageCriteria = useSAINUsageCriteriaMet(),
    fireSAINUsageEvent = _useSAINUsageCriteria.fireSAINUsageEvent;
  var _useAIAnswerContext2 = useAIAnswerContext(),
    smartAnswersRoute = _useAIAnswerContext2.smartAnswersRoute,
    commonAttributes = _useAIAnswerContext2.commonAttributes;
  var _useAIEventsInstrumen = useAIEventsInstrumentation(),
    trackAIResultView = _useAIEventsInstrumen.trackAIResultView;
  useEffect(function () {
    fg('kd_enable_new_usage_tracking') ? fireSAINUsageEvent({
      commonAttributes: commonAttributes
    }) : trackAIResultView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  switch (smartAnswersRoute) {
    case QueryIntent.TEAM:
      return /*#__PURE__*/React.createElement(TeamKnowledgeCard, null);
    case QueryIntent.PERSON:
      return /*#__PURE__*/React.createElement(PersonKnowledgeCard, null);
    default:
      return null;
  }
};
var AnswerCard = function AnswerCard() {
  var _useAIAnswerContext3 = useAIAnswerContext(),
    isAnswered = _useAIAnswerContext3.isAnswered,
    hasError = _useAIAnswerContext3.hasError,
    commonAttributes = _useAIAnswerContext3.commonAttributes,
    callbacks = _useAIAnswerContext3.callbacks,
    smartAnswersRoute = _useAIAnswerContext3.smartAnswersRoute;
  var answerFormat = commonAttributes.answerFormat,
    query = commonAttributes.query;
  var _useAnalytics3 = useAnalytics(),
    _useAnalytics4 = _slicedToArray(_useAnalytics3, 1),
    fireAnalyticsEvent = _useAnalytics4[0];
  var _useSAINUsageCriteria2 = useSAINUsageCriteriaMet(),
    fireSAINUsageEvent = _useSAINUsageCriteria2.fireSAINUsageEvent;
  var _useAIEventsInstrumen2 = useAIEventsInstrumentation(),
    trackAIResultAction = _useAIEventsInstrumen2.trackAIResultAction;
  var _useState = useState(true),
    _useState2 = _slicedToArray(_useState, 2),
    collapsed = _useState2[0],
    setCollapsed = _useState2[1];

  // reset on each new query
  useEffect(function () {
    setCollapsed(true);
  }, [query]);
  var toggleCollapsedState = function toggleCollapsedState() {
    if (collapsed) {
      fireSAINUsageEvent({
        metCriteria: SainAction.EXPAND_CLICKED,
        commonAttributes: commonAttributes
      });
    }
    setCollapsed(!collapsed);
  };
  var analyticsCallbacks = {
    onExpandedByDefault: function onExpandedByDefault() {
      return fireAnalyticsEvent(aiAnswerExpandShown(commonAttributes));
    },
    onExpandClicked: function onExpandClicked() {
      return fireAnalyticsEvent(aiAnswerExpandClicked(commonAttributes));
    },
    onCollapseClicked: function onCollapseClicked() {
      return fireAnalyticsEvent(aiAnswerCollapseClicked(commonAttributes));
    }
  };
  var sourceClickCallback = useCallback(function (index, hostname) {
    var _callbacks$onAIAnswer;
    fireAnalyticsEvent(onAIAnswerSourceClick(_objectSpread({
      sourceIndex: index,
      hostname: hostname
    }, commonAttributes)));
    fg('kd_enable_new_usage_tracking') ? fireSAINUsageEvent({
      metCriteria: SainAction.SOURCE_CLICKED,
      commonAttributes: commonAttributes
    }) : trackAIResultAction(SainAction.SOURCE_CLICKED);
    callbacks === null || callbacks === void 0 || (_callbacks$onAIAnswer = callbacks.onAIAnswerSourceClick) === null || _callbacks$onAIAnswer === void 0 || _callbacks$onAIAnswer.call(callbacks);
  }, [fireAnalyticsEvent, commonAttributes, fireSAINUsageEvent, callbacks, trackAIResultAction]);
  // TODO: Remove block when cleaning kd_smart_answers_render_by_experience fg
  if (isAnswered && !hasError && (smartAnswersRoute === QueryIntent.PERSON || smartAnswersRoute === QueryIntent.TEAM)) {
    return /*#__PURE__*/React.createElement(KnowledgeCard, null);
  }

  // TODO: Remove block when cleaning kd_smart_answers_render_by_experience fg
  // if there is no team found (either the team or its data)
  // don't render a smart answer card or an error state
  if (!!hasError && smartAnswersRoute === QueryIntent.TEAM) {
    return /*#__PURE__*/React.createElement(React.Fragment, null);
  }
  var answerContent = /*#__PURE__*/React.createElement(Collapse, {
    collapsed: collapsed,
    toggleCollapsedState: toggleCollapsedState,
    expandWhenClickedAnywhere: true,
    isLoading: false,
    callbacks: analyticsCallbacks
  }, /*#__PURE__*/React.createElement(AIAnswerBody, null));
  if (hasError) {
    answerContent = /*#__PURE__*/React.createElement(Box, {
      xcss: errorContainerStyles
    }, /*#__PURE__*/React.createElement(AIAnswerBody, null));
  } else if (answerFormat === NLPSearchResultFormat.JSON) {
    answerContent = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
      xcss: spacerStyles
    }), /*#__PURE__*/React.createElement(AIAnswerBody, null));
  }
  return /*#__PURE__*/React.createElement(AIAnswerSearchBorder, {
    isLoading: false,
    isStreaming: false
  }, answerContent, !hasError && /*#__PURE__*/React.createElement(Box, {
    xcss: sourcesContainerStyles,
    testId: "ai-answer-sources"
  }, /*#__PURE__*/React.createElement(AISources, {
    sources: commonAttributes.sources,
    searchSessionId: commonAttributes.searchSessionId,
    onClickHandler: sourceClickCallback,
    allowFreeflowLayout: true,
    limit: MAX_SOURCES
  })), /*#__PURE__*/React.createElement(SearchPageSmartAnswerFooter, null));
};
export var SmartAnswerCard = function SmartAnswerCard() {
  var _useAIAnswerContext4 = useAIAnswerContext(),
    loading = _useAIAnswerContext4.loading;
  return /*#__PURE__*/React.createElement(React.Fragment, null, loading ? /*#__PURE__*/React.createElement(SmartAnswerLoadingDialog, null) : /*#__PURE__*/React.createElement(AnswerCard, null));
};